// RealtimeAudioEngine (LUM-172 transport pivot)
//
// Client-owned audio for the WebSocket-relayed OpenAI Realtime transport.
// The WebRTC transport was abandoned because OpenAI clears output audio
// server-side at speech onset, unconditionally — fatal for backchannel-heavy
// interviews. Over the relay the browser owns BOTH directions:
//
//   mic → AudioWorklet capture → ~100ms Int16 PCM chunks → onMicChunk(base64)
//   server audio deltas (base64 PCM16 mono 24kHz) → enqueuePlayback → gapless
//     scheduled AudioBufferSourceNodes on a shared GainNode
//
// Because playback is local, barge-in becomes interruptPlayback(): instant
// client-side silence plus a sample-accurate played-ms figure for
// conversation.item.truncate — the server never decides what the user heard.
//
// onPlaybackStarted / onPlaybackStopped synthesize the WebRTC transport's
// output_audio_buffer.started/stopped events for the turn arbiter
// (realtime-barge-in.ts). `stopped` fires only when the scheduled queue
// FULLY drains (tail source's `ended` event), never per chunk, and is
// suppressed after interruptPlayback() — the interrupter already knows.
//
// The capture worklet is registered from an inline Blob URL module so the
// engine ships as a single file with no separate worklet asset to serve.

import { levelFromPcm16 } from '@/services/mic-level';
import { logger } from '@/utils/logger';

/** Realtime API audio format: PCM16 mono 24kHz. */
export const REALTIME_SAMPLE_RATE = 24_000;

/** Mic chunk size in samples: 100ms at 24kHz. */
export const CAPTURE_CHUNK_SAMPLES = 2_400;

/**
 * Lead time (seconds) when scheduling into an empty pipeline, so the first
 * chunk isn't clipped by starting in the past. One render quantum plus
 * slack; well under one chunk, so interrupt latency is unaffected.
 */
export const PLAYBACK_LEAD_SEC = 0.02;

/**
 * How long a drained playback queue stays "logically playing" before
 * onPlaybackStopped fires (LUM-176 #1). Network jitter drains the queue
 * for tens of milliseconds mid-answer; without the grace window every
 * micro-drain fired stopped/started pairs into the turn arbiter and
 * restarted the audible run (audible as odd breaks). A real end of turn
 * only loses 250ms of arbiter awareness — well under transcription lag.
 */
export const DRAIN_GRACE_MS = 250;

/** Ceiling for the adaptive jitter lead (see UnderrunRecovery). */
export const MAX_JITTER_LEAD_SEC = 0.32;

/** How long an AudioContext may sit 'suspended' before it counts as a
 *  mic loss the user must hear about. Startup and device-churn blips
 *  resolve in tens of milliseconds (12ms observed live, LUM-188);
 *  a real OS interruption (phone call, Siri) lasts seconds. */
export const SUSPEND_LOST_GRACE_MS = 1_500;

/**
 * Adaptive jitter-buffer policy (LUM-176 #1), pure for unit tests.
 * Every underrun doubles the scheduling lead — the silence deliberately
 * inserted before restarting a starved pipeline, trading a hair of
 * latency for headroom against the jitter the network just proved it
 * has. Never decays within a call: network quality is a call property.
 */
export class UnderrunRecovery {
  private underruns = 0;
  private lead = PLAYBACK_LEAD_SEC;

  get count(): number {
    return this.underruns;
  }

  get leadSec(): number {
    return this.lead;
  }

  /** Register one underrun; returns the new lead in seconds. */
  onUnderrun(): number {
    this.underruns += 1;
    this.lead = Math.min(this.lead * 2, MAX_JITTER_LEAD_SEC);
    return this.lead;
  }
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested; no Web Audio types)
// ---------------------------------------------------------------------------

/** Decode base64 PCM16LE into samples. A trailing odd byte is dropped. */
export function base64ToInt16(b64: string): Int16Array {
  const bin = atob(b64);
  const sampleCount = bin.length >> 1;
  const bytes = new Uint8Array(sampleCount * 2);
  for (let i = 0; i < bytes.length; i++) {
    // Binary-safe: atob output is latin1 (0-255), no surrogate pairs,
    // so codePointAt === charCodeAt here (Sonar S7758).
    bytes[i] = bin.codePointAt(i) ?? 0;
  }
  return new Int16Array(bytes.buffer);
}

/** Encode samples as base64 PCM16LE. */
export function int16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(
    samples.buffer,
    samples.byteOffset,
    samples.byteLength,
  );
  let bin = '';
  // String.fromCodePoint has an argument-count limit; encode in slabs.
  const SLAB = 0x8000;
  for (let i = 0; i < bytes.length; i += SLAB) {
    bin += String.fromCodePoint(...bytes.subarray(i, i + SLAB));
  }
  return btoa(bin);
}

/** Convert Int16 PCM to Float32 in [-1, 1] for AudioBuffer channel data. */
export function int16ToFloat32(samples: Int16Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    out[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
  }
  return out;
}

/** Convert Float32 [-1, 1] to Int16 PCM with clamping. */
export function float32ToInt16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Duration of a sample count at the realtime rate, in milliseconds. */
export function samplesToMs(
  sampleCount: number,
  sampleRate: number = REALTIME_SAMPLE_RATE,
): number {
  return (sampleCount / sampleRate) * 1000;
}

// ---------------------------------------------------------------------------
// Playback schedule tracker (pure state machine; time injected by caller)
// ---------------------------------------------------------------------------

interface ScheduledItem {
  itemId: string;
  /** Context time the item's first chunk starts. */
  start: number;
  /** Context time the item's last scheduled chunk ends. */
  end: number;
}

export interface ScheduleResult {
  /** Context time at which the caller must start the source node. */
  startAt: number;
  /** True when this chunk begins a new audible run (silent → playing). */
  startedFromSilence: boolean;
}

export interface InterruptResult {
  itemId: string | null;
  playedMs: number;
}

/** One playback starvation event (LUM-176 #1), for forensic telemetry. */
export interface PlaybackUnderrun {
  itemId: string;
  /** How long the pipeline sat silent before audio resumed. */
  gapMs: number;
  /** Underruns so far this call. */
  count: number;
  /** The adaptive jitter lead now in force. */
  leadMs: number;
}

/**
 * Tracks contiguous chunk scheduling against an injected clock so the
 * played-ms math is pure and unit-testable. Chunks for one item are
 * contiguous (the Realtime API streams deltas in order), so per-item played
 * duration is `clamp(now - itemStart, 0, itemDuration)`.
 */
export class PlaybackScheduleTracker {
  private items: ScheduledItem[] = [];
  private scheduledUntil = 0;
  private leadSec: number;

  constructor(leadSec: number = PLAYBACK_LEAD_SEC) {
    this.leadSec = leadSec;
  }

  /** Raise the scheduling lead (adaptive jitter buffer, LUM-176 #1). */
  setLead(leadSec: number): void {
    this.leadSec = leadSec;
  }

  /** Register a chunk of `durationSec` for `itemId` at time `now`. */
  schedule(itemId: string, durationSec: number, now: number): ScheduleResult {
    const drained = this.isDrained(now);
    const startAt = drained ? now + this.leadSec : this.scheduledUntil;
    if (drained) {
      // A drained pipeline starts a fresh audible run; prior accounting is
      // history and would otherwise leak into playedAt/interrupt lookups.
      this.items = [];
    }
    const last = this.items[this.items.length - 1];
    if (last && last.itemId === itemId) {
      last.end = startAt + durationSec;
    } else {
      this.items.push({ itemId, start: startAt, end: startAt + durationSec });
    }
    this.scheduledUntil = startAt + durationSec;
    return { startAt, startedFromSilence: drained };
  }

  /** True when nothing is scheduled at or after `now`. */
  isDrained(now: number): boolean {
    return this.scheduledUntil <= now;
  }

  /** The item audible at `now`, or null when drained / not yet started. */
  playingItemAt(now: number): string | null {
    for (const item of this.items) {
      if (now >= item.start && now < item.end) return item.itemId;
    }
    return null;
  }

  /**
   * Flush the schedule. Returns the item audible at `now` and how many
   * milliseconds of it actually played (context time elapsed since its
   * first chunk started, clamped to what was scheduled). When the run was
   * still inside the start lead, the current item is reported with 0ms.
   */
  interrupt(now: number): InterruptResult {
    let result: InterruptResult = { itemId: null, playedMs: 0 };
    const audible = this.playingItemAt(now);
    const target = audible
      ? this.items.find(i => i.itemId === audible)
      : // Inside the lead window: first scheduled-but-unstarted item.
        this.items.find(i => i.start > now);
    if (target && !this.isDrained(now)) {
      const playedSec = Math.min(
        Math.max(now - target.start, 0),
        target.end - target.start,
      );
      result = { itemId: target.itemId, playedMs: playedSec * 1000 };
    }
    this.items = [];
    this.scheduledUntil = 0;
    return result;
  }
}

// ---------------------------------------------------------------------------
// Capture worklet (inline module, registered via Blob URL)
// ---------------------------------------------------------------------------

// Accumulates 128-sample render quanta into CAPTURE_CHUNK_SAMPLES Int16
// chunks and transfers each full chunk to the main thread. Runs at the
// context rate (24kHz), so no resampling here — the MediaStreamSourceNode
// already resampled the mic.
const CAPTURE_WORKLET_NAME = 'dd-realtime-capture';

const CAPTURE_WORKLET_SOURCE = `
class DdRealtimeCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Int16Array(${CAPTURE_CHUNK_SAMPLES});
    this.len = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        let s = ch[i];
        s = s < -1 ? -1 : s > 1 ? 1 : s;
        this.buf[this.len++] = s < 0 ? s * 0x8000 : s * 0x7fff;
        if (this.len === this.buf.length) {
          const out = this.buf.slice(0);
          this.port.postMessage(out.buffer, [out.buffer]);
          this.len = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('${CAPTURE_WORKLET_NAME}', DdRealtimeCapture);
`;

// AudioContext.setSinkId is Chromium-only for now; feature-detect.
type ContextWithSinkId = AudioContext & {
  setSinkId?: (id: string) => Promise<void>;
};

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class RealtimeAudioEngine {
  /** Continuous mic PCM: base64 PCM16 mono 24kHz, ~100ms per call. */
  onMicChunk: ((b64: string) => void) | null = null;
  /** Level of each capture chunk on the shared mic scale (LUM-82):
   *  numbers only, for the in-call indicator and health checks. */
  onMicLevel: ((level: number) => void) | null = null;
  /** Audible output transitioned silent → playing. */
  onPlaybackStarted: (() => void) | null = null;
  /** Queue stayed drained through the full grace window (LUM-176 #1) —
   *  a logical end of playback, never a jitter micro-drain; suppressed
   *  after interrupt. */
  onPlaybackStopped: (() => void) | null = null;
  /** Playback starved mid-item and resumed within the grace window. */
  onUnderrun: ((info: PlaybackUnderrun) => void) | null = null;
  /** Mic/context lifecycle telemetry (LUM-187 pass 2): 'lost' fires
   *  when capture died and recovery failed — server-side that is
   *  indistinguishable from a silent user, so the UI must say it.
   *  'recovered' fires when a dead track/context was rebuilt. */
  onMicEvent:
    | ((kind: 'lost' | 'recovered' | 'output-fallback', detail: string) => void)
    | null = null;

  private ctx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private playbackGain: GainNode | null = null;
  private workletUrl: string | null = null;

  private inputDeviceId: string | null = null;
  private outputDeviceId: string | null = null;

  private readonly tracker = new PlaybackScheduleTracker();
  private readonly recovery = new UnderrunRecovery();
  private tailSource: AudioBufferSourceNode | null = null;
  private activeSources = new Set<AudioBufferSourceNode>();
  /** Bumped on interrupt/stop so stale `ended` handlers can't fire drain. */
  private generation = 0;
  /** Cancels the pending drain-grace stopped-fire, if armed. */
  private cancelDrainGrace: (() => void) | null = null;
  /** Item whose tail drained while the grace window is open. */
  private drainedItemId: string | null = null;
  private drainedAtMs = 0;
  /** Armed while the context sits 'suspended'; fires the 'lost'
   *  report after SUSPEND_LOST_GRACE_MS (LUM-188). */
  private suspendLostTimer: number | null = null;
  private suspendReported = false;

  // Lazy by contract: no AudioContext until start() (autoplay policy).

  async start(inputDeviceId?: string): Promise<void> {
    if (this.ctx) return;
    if (inputDeviceId) this.inputDeviceId = inputDeviceId;

    // Same constraints as the WebRTC service and InterviewPage's pre-flight
    // permission probe.
    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: this.inputDeviceId
          ? { exact: this.inputDeviceId }
          : undefined,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    const ctx = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
    this.ctx = ctx;
    this.applyOutputDevice();

    this.workletUrl = URL.createObjectURL(
      new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' }),
    );
    await ctx.audioWorklet.addModule(this.workletUrl);

    this.watchMicTracks(this.micStream);
    this.captureNode = new AudioWorkletNode(ctx, CAPTURE_WORKLET_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
    });
    this.captureNode.port.onmessage = (ev: MessageEvent<ArrayBuffer>) => {
      const samples = new Int16Array(ev.data);
      this.onMicLevel?.(levelFromPcm16(samples));
      const cb = this.onMicChunk;
      if (!cb) return;
      cb(int16ToBase64(samples));
    };
    // A worklet exception kills capture permanently and silently —
    // rebuild the whole mic path (LUM-187 pass 2).
    this.captureNode.onprocessorerror = () => {
      logger.error('RealtimeAudioEngine: capture worklet crashed');
      void this.reacquireMic('worklet-error');
    };
    this.micSource = ctx.createMediaStreamSource(this.micStream);
    this.micSource.connect(this.captureNode);
    // iOS/macOS suspend the context on phone calls, Siri, or device
    // churn ('interrupted' surfaces as 'suspended' in the type). A
    // suspended context stops BOTH capture and playback; resume it the
    // moment the OS allows (LUM-187 pass 2). Suspensions are routinely
    // TRANSIENT — a 12ms blip at call start reached the UI as
    // "microphone lost" on the first real-dev drive (LUM-188) — so
    // 'lost' fires only when the context is still suspended after a
    // grace, and 'recovered' only after a loss was actually reported.
    ctx.addEventListener('statechange', () => {
      if (ctx !== this.ctx) return; // stopped/replaced
      if (ctx.state === 'suspended') {
        if (this.suspendLostTimer === null) {
          this.suspendLostTimer = window.setTimeout(() => {
            this.suspendReported = true;
            this.onMicEvent?.('lost', 'context-suspended');
          }, SUSPEND_LOST_GRACE_MS);
        }
        void ctx.resume().catch(() => {
          // resume denied now; retry on the next statechange/OS signal
        });
      } else if (ctx.state === 'running') {
        if (this.suspendLostTimer !== null) {
          clearTimeout(this.suspendLostTimer);
          this.suspendLostTimer = null;
        }
        if (this.suspendReported) {
          this.suspendReported = false;
          this.onMicEvent?.('recovered', 'context-resumed');
        }
      }
    });
    // Keep the worklet pulled by the graph without making the mic audible:
    // route through a muted gain to the destination.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    this.captureNode.connect(mute).connect(ctx.destination);

    this.playbackGain = ctx.createGain();
    this.playbackGain.connect(ctx.destination);

    navigator.mediaDevices.addEventListener(
      'devicechange',
      this.onDeviceChange,
    );

    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }

  /** Append a base64 PCM16 chunk to the gapless playback pipeline. */
  enqueuePlayback(b64: string, itemId: string): void {
    const ctx = this.ctx;
    const gain = this.playbackGain;
    if (!ctx || !gain) {
      logger.warn('RealtimeAudioEngine: enqueuePlayback before start; dropped');
      return;
    }
    const samples = base64ToInt16(b64);
    if (samples.length === 0) return;

    const buffer = ctx.createBuffer(1, samples.length, REALTIME_SAMPLE_RATE);
    buffer.copyToChannel(int16ToFloat32(samples), 0);

    // A pending drain grace means the queue starved moments ago. More
    // audio for the SAME item = an underrun, not a turn boundary: keep
    // the logical run alive (no stopped/started churn into the turn
    // arbiter) and widen the jitter lead. A DIFFERENT item means the
    // previous turn really ended — settle its stopped event first.
    let resumedFromUnderrun = false;
    if (this.cancelDrainGrace) {
      this.cancelDrainGrace();
      this.cancelDrainGrace = null;
      if (this.drainedItemId === itemId) {
        resumedFromUnderrun = true;
        const gapMs = performance.now() - this.drainedAtMs;
        const leadSec = this.recovery.onUnderrun();
        this.tracker.setLead(leadSec);
        this.onUnderrun?.({
          itemId,
          gapMs,
          count: this.recovery.count,
          leadMs: leadSec * 1000,
        });
      } else {
        this.onPlaybackStopped?.();
      }
      this.drainedItemId = null;
    }

    const now = ctx.currentTime;
    const { startAt, startedFromSilence } = this.tracker.schedule(
      itemId,
      buffer.duration,
      now,
    );

    if (startedFromSilence) {
      // New audible run: restore gain (an earlier interrupt zeroed it) and
      // invalidate any stale ended handlers.
      this.generation++;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(1, now);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);

    const gen = this.generation;
    this.activeSources.add(source);
    this.tailSource = source;
    source.onended = () => {
      this.activeSources.delete(source);
      if (gen !== this.generation) return; // interrupted/stopped
      if (source !== this.tailSource) return; // not the drain point
      this.tailSource = null;
      this.armDrainGrace(itemId);
    };

    source.start(startAt);
    if (startedFromSilence && !resumedFromUnderrun) {
      this.onPlaybackStarted?.();
    }
  }

  /**
   * The scheduled queue just drained. Hold the stopped event for
   * DRAIN_GRACE_MS: if more audio for the same item lands first this was
   * network starvation (enqueuePlayback resumes the run silently); only
   * sustained silence is reported as end of playback.
   */
  private armDrainGrace(itemId: string): void {
    this.cancelDrainGrace?.();
    this.drainedItemId = itemId;
    this.drainedAtMs = performance.now();
    const timer = setTimeout(() => {
      this.cancelDrainGrace = null;
      this.drainedItemId = null;
      this.onPlaybackStopped?.();
    }, DRAIN_GRACE_MS);
    this.cancelDrainGrace = () => clearTimeout(timer);
  }

  /**
   * Instantly silence output and flush the queue. Returns the item that was
   * audible and how many milliseconds of it actually played, for
   * conversation.item.truncate. Does NOT fire onPlaybackStopped — the
   * caller initiated the stop and synthesizes its own cleared event.
   */
  interruptPlayback(): InterruptResult {
    const ctx = this.ctx;
    if (!ctx) return { itemId: null, playedMs: 0 };

    // Audible position, not scheduler position: device output lags
    // AudioContext.currentTime by base+output latency (Bluetooth can be
    // 100-300ms). Under-truncate rather than delete audio the user
    // never heard (Codex review finding 7).
    const latency =
      (ctx.baseLatency ?? 0) +
      ('outputLatency' in ctx ? (ctx.outputLatency ?? 0) : 0);
    const audibleNow = Math.max(0, ctx.currentTime - latency);
    const result = this.tracker.interrupt(audibleNow);
    const now = ctx.currentTime;

    // Invalidate ended handlers BEFORE stopping so none fires a drain,
    // and cancel a pending drain grace — the caller synthesizes its own
    // cleared event; a late stopped would contradict it.
    this.generation++;
    this.tailSource = null;
    this.cancelDrainGrace?.();
    this.cancelDrainGrace = null;
    this.drainedItemId = null;

    // Hard-mute first — sample-accurate silence even before each source's
    // stop() takes effect.
    if (this.playbackGain) {
      this.playbackGain.gain.cancelScheduledValues(now);
      this.playbackGain.gain.setValueAtTime(0, now);
    }
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // Never started or already stopped — nothing to do.
      }
      source.disconnect();
    }
    this.activeSources.clear();

    return result;
  }

  setInputDevice(deviceId: string): void {
    this.inputDeviceId = deviceId;
    const ctx = this.ctx;
    if (!ctx || !this.captureNode) return;
    void (async () => {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        this.adoptMicStream(ctx, newStream);
      } catch (err) {
        logger.warn('RealtimeAudioEngine: input device switch failed', {
          err,
        });
      }
    })();
  }

  /** Swap the live capture input to `stream`, stopping the old tracks
   *  and re-arming the track-ended watch. */
  private adoptMicStream(ctx: AudioContext, stream: MediaStream): void {
    this.micSource?.disconnect();
    for (const track of this.micStream?.getTracks() ?? []) {
      track.stop();
    }
    this.micStream = stream;
    this.watchMicTracks(stream);
    this.micSource = ctx.createMediaStreamSource(stream);
    if (this.captureNode) {
      this.micSource.connect(this.captureNode);
    }
  }

  /** A Bluetooth headset powering off or a USB mic unplug ends the
   *  track SILENTLY — the graph keeps running on dead input and the
   *  interview just hears silence (LUM-187 pass 2). Watch every track
   *  and rebuild capture when one dies. */
  private watchMicTracks(stream: MediaStream): void {
    for (const track of stream.getTracks()) {
      track.addEventListener('ended', () => {
        if (stream !== this.micStream) return; // already replaced
        logger.warn('RealtimeAudioEngine: mic track ended');
        void this.reacquireMic('track-ended');
      });
    }
  }

  /** Rebuild the capture path after a dead track/worklet: try the
   *  selected device first, then the platform default — the selected
   *  one may be exactly what vanished. */
  private async reacquireMic(reason: string): Promise<void> {
    const ctx = this.ctx;
    if (!ctx || !this.captureNode) return;
    const candidates: (ConstrainDOMString | undefined)[] = this.inputDeviceId
      ? [{ exact: this.inputDeviceId }, undefined]
      : [undefined];
    for (const deviceId of candidates) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        this.adoptMicStream(ctx, stream);
        this.onMicEvent?.('recovered', reason);
        return;
      } catch {
        // next candidate
      }
    }
    logger.error('RealtimeAudioEngine: mic reacquire failed', { reason });
    this.onMicEvent?.('lost', reason);
  }

  setOutputDevice(deviceId: string): void {
    this.outputDeviceId = deviceId;
    this.applyOutputDevice();
  }

  private applyOutputDevice(): void {
    if (!this.ctx || !this.outputDeviceId) return;
    const ctx = this.ctx as ContextWithSinkId;
    if (typeof ctx.setSinkId === 'function') {
      ctx.setSinkId(this.outputDeviceId).catch(err => {
        logger.warn('RealtimeAudioEngine: setSinkId failed', { err });
      });
    }
    // Non-Chromium: graceful no-op; output stays on the default device.
  }

  /** A selected output sink that vanishes (Bluetooth headset off)
   *  leaves playback routed at a dead device on some platforms — the
   *  UI still says 'speaking' while the user hears nothing (LUM-187
   *  pass 2, Codex finding 5). On devicechange, fall back to the
   *  system default when the chosen sink is gone. */
  private readonly onDeviceChange = () => {
    const wanted = this.outputDeviceId;
    if (!this.ctx || !wanted) return;
    void navigator.mediaDevices.enumerateDevices().then(devices => {
      if (wanted !== this.outputDeviceId || !this.ctx) return; // changed since
      const stillThere = devices.some(
        d => d.kind === 'audiooutput' && d.deviceId === wanted,
      );
      if (stillThere) return;
      logger.warn('RealtimeAudioEngine: output sink vanished, using default');
      this.outputDeviceId = null;
      const ctx = this.ctx as ContextWithSinkId;
      if (typeof ctx.setSinkId === 'function') {
        ctx.setSinkId('').catch(() => {
          // default sink rejection — nothing further to fall back to
        });
      }
      this.onMicEvent?.('output-fallback', wanted);
    });
  };

  /** Release the mic and close the context. Safe to call repeatedly. */
  stop(): void {
    this.generation++;
    if (this.suspendLostTimer !== null) {
      clearTimeout(this.suspendLostTimer);
      this.suspendLostTimer = null;
    }
    this.suspendReported = false;
    this.tracker.interrupt(this.ctx?.currentTime ?? 0);
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // Never started — fine.
      }
      source.disconnect();
    }
    this.activeSources.clear();
    this.tailSource = null;
    this.cancelDrainGrace?.();
    this.cancelDrainGrace = null;
    this.drainedItemId = null;

    if (this.captureNode) {
      this.captureNode.port.onmessage = null;
      this.captureNode.disconnect();
      this.captureNode = null;
    }
    this.micSource?.disconnect();
    this.micSource = null;
    for (const track of this.micStream?.getTracks() ?? []) {
      track.stop();
    }
    this.micStream = null;
    this.playbackGain?.disconnect();
    this.playbackGain = null;

    if (this.workletUrl) {
      URL.revokeObjectURL(this.workletUrl);
      this.workletUrl = null;
    }
    navigator.mediaDevices.removeEventListener(
      'devicechange',
      this.onDeviceChange,
    );
    if (this.ctx) {
      this.ctx.close().catch(() => {
        // Already closed.
      });
      this.ctx = null;
    }
  }
}

export default RealtimeAudioEngine;
